import { Character, GetUserCharactersGQL, UpdateCharacterGQL, CreateCharacterGQL, GetCharactersForUsersGQL } from 'generated/graphql';
import { State, Action, StateContext, Selector, select } from '@ngxs/store';
import { makeDefaultEntitiesState, makeBaseEntitiesState } from '@modules/app-common/state/entities.state';
import { Injectable, inject } from '@angular/core';
import { EMPTY, tap } from 'rxjs';
import { LoggedInUserState } from '@modules/auth/store';
import { MutationResult } from 'apollo-angular';

export { Character } from 'generated/graphql';

export class CharactersFindByUserAction {
  static readonly type = '[Characters] Find All My Characters';

  constructor() {}
}

export class CharactersSaveAction {
  static readonly type = '[Characters] Save';

  constructor(
    public payload: {
      character: Partial<Character>;
    }
  ) {}
}

export class CharactersBulkAddAction {
  static readonly type = '[Characters] Bulk Add';

  constructor(
    public payload: {
      characters: Character[];
    }
  ) {}
}

export class CharactersFindForUsersAction {
  static readonly type = '[Characters] Find For Users';

  constructor(
    public payload: {
      userIds: string[];
    }
  ) {}
}

const CharactersBaseEntitiesState = makeBaseEntitiesState<Character>('characters');

@State({
  name: 'characters',
  defaults: makeDefaultEntitiesState(),
})
@Injectable()
export class CharactersEntitiesState extends CharactersBaseEntitiesState {
  private createCharacterGQL = inject(CreateCharacterGQL);
  private updateCharacterGQL = inject(UpdateCharacterGQL);
  private getUserCharactersGQL = inject(GetUserCharactersGQL);
  private getCharactersForUsersGQL = inject(GetCharactersForUsersGQL);

  @Selector()
  static myCharacters(state: { entityMap: Record<string, Character> }): (userId: string) => Character[] {
    return (userId: string) => {
      return Object.values(state?.entityMap ?? {}).filter((entity) => entity.userId === userId);
    };
  }

  @Action(CharactersFindByUserAction)
  findUserCharacters(ctx: StateContext<any>) {
    return this.getUserCharactersGQL.fetch({}, { fetchPolicy: 'no-cache' }).pipe(
      tap((result) => {
        this.patchEntities(ctx, result.data.getUserCharacters as Character[]);
      })
    );
  }

  @Action(CharactersBulkAddAction)
  bulkAddCharacters(ctx: StateContext<any>, action: CharactersBulkAddAction) {
    this.patchEntities(ctx, action.payload.characters);
  }

  @Action(CharactersFindForUsersAction)
  findCharactersForUsers(ctx: StateContext<any>, action: CharactersFindForUsersAction) {
    const { userIds } = action.payload;

    if (!userIds.length) {
      return EMPTY;
    }

    return this.getCharactersForUsersGQL.fetch({ userIds }, { fetchPolicy: 'no-cache' }).pipe(
      tap((result) => {
        ctx.dispatch(new CharactersBulkAddAction({ characters: result.data.getCharactersForUsers as Character[] }));
      })
    );
  }

  @Action(CharactersSaveAction)
  saveCharacter(ctx: StateContext<any>, action: CharactersSaveAction) {
    const saveData = action.payload.character;
    const isUpdate = !!saveData._id;

    const dto: Record<string, unknown> = {
      bodyTypeKey: saveData.bodyTypeKey || '',
      hairColorKey: saveData.hairColorKey || '',
      skinToneKey: saveData.skinToneKey || '',
      hairStyleKey: saveData.hairStyleKey || '',
      faceAccessoryKey: saveData.faceAccessoryKey || '',
    };
    if (saveData.characterProfileImageUrl !?? undefined) {
      dto.characterProfileImageUrl = saveData.characterProfileImageUrl;
    }
    if (saveData.characterPaperDollImageUrl !?? undefined) {
      dto.characterPaperDollImageUrl = saveData.characterPaperDollImageUrl;
    }

    if (isUpdate) {
      return this.updateCharacterGQL.mutate({ dto: { _id: saveData._id, ...dto } }, { context: { ignoreLoading: true } }).pipe(
        tap((result: MutationResult) => {
          this.patchEntity(ctx, result?.data?.updateCharacter as Character);
        })
      );
    } else {
      return this.createCharacterGQL.mutate({ dto }, { context: { ignoreLoading: true } }).pipe(
        tap((result: MutationResult) => {
          this.patchEntity(ctx, result?.data?.createCharacter as Character);
        })
      );
    }
  }
}
